import { FC, KeyboardEvent } from 'react';
import { Button, Input } from 'reactstrap';
import { SearchModeControlProps, SearchMode } from './types';
import { TOP_N_OPTIONS } from './utils';

/**
 * Search Pool mode control (README §Search Pool): segmented Boolean /
 * Semantic / Hybrid (default Hybrid), a one-line explainer, and the query
 * input + Search button. No AI-explain / must-have terms here — those are
 * Batch-only (BatchControlCard). Search runs only on explicit submit (Search
 * button or Enter), never live-as-you-type — index.tsx owns the fetch.
 */

const MODES: { key: SearchMode; label: string }[] = [
  { key: 'boolean', label: 'Boolean' },
  { key: 'semantic', label: 'Semantic' },
  { key: 'hybrid', label: 'Hybrid' },
];

const MODE_EXPLAINERS: Record<SearchMode, string> = {
  boolean:
    'Exact keyword matching with AND / OR / NOT operators — ranked by most recently updated, no relevance score.',
  semantic: 'Finds candidates by meaning rather than exact words, ranked by similarity.',
  hybrid: 'Combines keyword precision with semantic ranking (recommended).',
};

const MAX_QUERY_LEN = 2000;

// TOP_N_OPTIONS (utils.ts) is shared with the Batch control's Results select —
// topN is shared state, so a Top-N chosen on either tab carries over to the
// other, and the saved-settings sanitizer snaps stored values onto this list.

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
};

const SEGMENT_WRAP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  border: '1px solid #E2E5EA',
  borderRadius: 4,
  overflow: 'hidden',
};

const segmentButtonStyle = (active: boolean): React.CSSProperties => ({
  border: 'none',
  background: active ? '#EAA827' : '#fff',
  color: active ? '#1F2937' : '#667085',
  fontWeight: active ? 700 : 500,
  fontSize: 13,
  padding: '7px 16px',
  cursor: 'pointer',
});

const SearchModeControl: FC<SearchModeControlProps> = ({ mode, query, topN, onMode, onQuery, onTopN, onSearch }) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  return (
    <div style={CARD_STYLE}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={SEGMENT_WRAP_STYLE} role="tablist" aria-label="Search mode">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={mode === m.key}
              style={segmentButtonStyle(mode === m.key)}
              onClick={() => onMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#667085', margin: 0 }}>Results</label>
          <Input
            type="select"
            value={topN}
            onChange={(e) => onTopN(Number(e.target.value))}
            style={{ height: 34, width: 100, fontSize: 13 }}
          >
            {TOP_N_OPTIONS.map((n) => (
              <option key={n} value={n}>
                Top {n}
              </option>
            ))}
          </Input>
        </div>
      </div>

      <div style={{ fontSize: 12, color: '#667085', marginTop: 8, marginBottom: 14 }}>
        {MODE_EXPLAINERS[mode]}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          type="text"
          value={query}
          maxLength={MAX_QUERY_LEN}
          placeholder='e.g. "senior java" AND (aws OR gcp) NOT contract'
          onChange={(e) => onQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ height: 38, fontSize: 13 }}
        />
        <Button
          type="button"
          onClick={onSearch}
          disabled={!query.trim()}
          style={{ background: '#EAA827', borderColor: '#EAA827', color: '#1F2937', fontWeight: 600 }}
        >
          Search
        </Button>
      </div>
    </div>
  );
};

export default SearchModeControl;
